import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "../lib/api";
import { formatearMoneda, formatearHora } from "../lib/formato";
import type { BancoNotificacion } from "@tentacion/shared";

interface PagoBancarioItem {
  id: string;
  banco: BancoNotificacion;
  monto: number | string;
  remitente?: string | null;
  referencia?: string | null;
  emailOrigen?: string | null;
  recibidoEn: string;
  estado: string;
}

export interface ModalVincularPagoBancarioProps {
  abierto: boolean;
  facturaId?: string;
  montoFactura?: number;
  onCerrar: () => void;
  onAsociado?: () => void;
}

const BADGES_BANCO: Record<BancoNotificacion, { bg: string; label: string; icono: string }> = {
  NEQUI: {
    bg: "bg-purple-100 text-purple-800 border-purple-300 ring-purple-500/20",
    label: "Nequi",
    icono: "🟣",
  },
  BANCOLOMBIA: {
    bg: "bg-amber-100 text-amber-900 border-amber-300 ring-amber-500/20",
    label: "Bancolombia",
    icono: "🟡",
  },
  DAVIPLATA: {
    bg: "bg-rose-100 text-rose-800 border-rose-300 ring-rose-500/20",
    label: "DaviPlata",
    icono: "🔴",
  },
  OTRO: {
    bg: "bg-blue-100 text-blue-800 border-blue-300 ring-blue-500/20",
    label: "Banco",
    icono: "🏦",
  },
};

export default function ModalVincularPagoBancario({
  abierto,
  facturaId,
  montoFactura,
  onCerrar,
  onAsociado,
}: ModalVincularPagoBancarioProps) {
  const [pagos, setPagos] = useState<PagoBancarioItem[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesandoId, setProcesandoId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargarPagosPendientes = async () => {
    try {
      setCargando(true);
      setError(null);
      const res = await apiFetch<PagoBancarioItem[]>("/pagos-bancarios?estado=PENDIENTE");
      setPagos(res || []);
    } catch (err: any) {
      setError(err?.message || "No se pudieron cargar los pagos bancarios pendientes");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (abierto) {
      cargarPagosPendientes();
    }
  }, [abierto]);

  if (!abierto) return null;

  const asociarPago = async (pagoId: string) => {
    if (!facturaId) {
      setError("No hay una factura seleccionada para vincular el pago.");
      return;
    }
    try {
      setProcesandoId(pagoId);
      setError(null);
      await apiFetch(`/pagos-bancarios/${pagoId}/asociar`, {
        method: "POST",
        body: { facturaId },
      });
      if (onAsociado) onAsociado();
      onCerrar();
    } catch (err: any) {
      setError(err?.message || "Error al vincular la transferencia");
    } finally {
      setProcesandoId(null);
    }
  };

  const ignorarPago = async (pagoId: string) => {
    try {
      setProcesandoId(pagoId);
      setError(null);
      await apiFetch(`/pagos-bancarios/${pagoId}/ignorar`, {
        method: "POST",
      });
      await cargarPagosPendientes();
    } catch (err: any) {
      setError(err?.message || "Error al descartar la transferencia");
    } finally {
      setProcesandoId(null);
    }
  };

  const confirmarPago = async (pagoId: string) => {
    try {
      setProcesandoId(pagoId);
      setError(null);
      await apiFetch(`/pagos-bancarios/${pagoId}/confirmar`, {
        method: "POST",
      });
      await cargarPagosPendientes();
    } catch (err: any) {
      setError(err?.message || "Error al confirmar la transferencia");
    } finally {
      setProcesandoId(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-superficie p-6 shadow-2xl border border-borde space-y-4 my-auto">
        {/* ENCABEZADO */}
        <div className="flex items-center justify-between border-b border-borde/60 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 text-xl font-bold border border-emerald-500/20 shadow-inner">
              💰
            </div>
            <div>
              <h3 className="font-display text-lg font-black text-texto">
                Transferencias Recibidas
              </h3>
              <p className="text-xs text-texto-secundario font-medium">
                {montoFactura !== undefined
                  ? `Vincular a la cuenta actual (${formatearMoneda(montoFactura)})`
                  : "Selecciona una transferencia para vincular"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            className="flex h-8 w-8 items-center justify-center rounded-xl bg-borde/40 text-texto-secundario hover:bg-borde hover:text-texto transition-colors"
          >
            ✕
          </button>
        </div>

        {/* MENSAJE DE ERROR */}
        {error && (
          <div className="rounded-2xl bg-rose-500/10 p-3 text-xs text-rose-600 border border-rose-500/20 font-medium">
            ⚠️ {error}
          </div>
        )}

        {/* LISTADO DE PAGOS */}
        <div className="max-h-80 overflow-y-auto space-y-3 pr-1">
          {cargando ? (
            <div className="py-8 text-center text-xs text-texto-secundario animate-pulse">
              Buscando transferencias en el correo...
            </div>
          ) : pagos.length === 0 ? (
            <div className="py-8 text-center space-y-2">
              <span className="text-3xl">📭</span>
              <p className="text-xs font-semibold text-texto-secundario">
                No hay transferencias pendientes sin vincular.
              </p>
              <p className="text-[11px] text-texto-secundario/70 max-w-xs mx-auto">
                Las notificaciones por correo de Nequi, Bancolombia o Daviplata aparecerán aquí automáticamente al recibirlas.
              </p>
            </div>
          ) : (
            pagos.map((pago) => {
              const configBanco = BADGES_BANCO[pago.banco] ?? BADGES_BANCO.OTRO;
              const numMonto = Number(pago.monto);
              const esMontoExacto = montoFactura !== undefined && Math.abs(numMonto - montoFactura) < 1;

              return (
                <div
                  key={pago.id}
                  className={`relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-2xl border transition-all ${
                    esMontoExacto
                      ? "bg-emerald-500/5 border-emerald-500/30 ring-2 ring-emerald-500/20"
                      : "bg-superficie-elevada border-borde hover:border-borde-fuerte"
                  }`}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-0.5 text-[11px] font-bold ${configBanco.bg}`}
                      >
                        {configBanco.icono} {configBanco.label}
                      </span>

                      {esMontoExacto && (
                        <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/10 px-2 py-0.5 text-[10px] font-black text-emerald-600 border border-emerald-500/20">
                          ✨ Monto Exacto
                        </span>
                      )}
                    </div>

                    <div className="font-display text-base font-black text-texto">
                      {formatearMoneda(numMonto)}
                    </div>

                    <div className="text-xs text-texto-secundario font-medium">
                      {pago.remitente ? (
                        <span>
                          De: <strong className="text-texto">{pago.remitente}</strong>
                        </span>
                      ) : (
                        <span>Remitente no especificado</span>
                      )}
                      {pago.referencia && (
                        <span className="ml-2 text-texto-secundario/80">
                          • Ref: <code>{pago.referencia}</code>
                        </span>
                      )}
                    </div>

                    <div className="text-[10px] text-texto-secundario/60">
                      Recibido: {formatearHora(pago.recibidoEn, { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>

                  {/* ACCIONES */}
                  <div className="flex items-center gap-2 w-full sm:w-auto pt-2 sm:pt-0">
                    <button
                      type="button"
                      disabled={procesandoId === pago.id}
                      onClick={() => ignorarPago(pago.id)}
                      className="px-3 py-2 text-[11px] font-bold text-texto-secundario hover:text-rose-600 hover:bg-rose-500/10 rounded-xl transition-colors"
                      title="Descartar esta notificación"
                    >
                      Descartar
                    </button>

                    {facturaId ? (
                      <button
                        type="button"
                        disabled={procesandoId === pago.id}
                        onClick={() => asociarPago(pago.id)}
                        className="flex-1 sm:flex-initial px-4 py-2.5 text-xs font-black text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-lg shadow-emerald-600/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {procesandoId === pago.id ? "Vinculando..." : "Vincular Pago"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={procesandoId === pago.id}
                        onClick={() => confirmarPago(pago.id)}
                        title="Marcar como pago real confirmado (sin ligarlo a una venta todavía)"
                        className="flex-1 sm:flex-initial px-4 py-2.5 text-xs font-black text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl shadow-lg shadow-emerald-600/20 transition-all hover:scale-[1.02] active:scale-[0.98]"
                      >
                        {procesandoId === pago.id ? "Confirmando..." : "✓ Confirmar"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* REFRESCAR / CERRAR */}
        <div className="flex items-center justify-between border-t border-borde/60 pt-3">
          <button
            type="button"
            onClick={cargarPagosPendientes}
            className="text-xs font-semibold text-magenta hover:underline flex items-center gap-1"
          >
            🔄 Actualizar lista
          </button>

          <button
            type="button"
            onClick={onCerrar}
            className="px-5 py-2.5 text-xs font-bold text-texto-secundario bg-borde/30 hover:bg-borde rounded-xl transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
